import React, { useRef } from 'react';
import classNames from 'classnames';
import { ConnectionSearchOptions, FilterOption } from '@css-xp/graphtypes/src/generated-types-sxp';
import {
  DEFAULT_PAGE_SIZE,
  QUOTE_TABLE_DEFAULT_SORT_OPTIONS,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { useFeatureToggle } from '@css-xp/base';
import { QuoteTable, QuoteTableColumnsType } from './quote-table';
import { getDefaultFilterOptions } from '../utils/quote-utils';
import { useFetchQuotesForTransactionAccount } from './hooks/use-fetch-quotes-for-transaction-account';

interface QuoteTableContainerProps {
  transactionAccountId: string;
  label?: string;
  filterOptions?: FilterOption;
  // hiddenColumns is used to hide columns from the table
  hiddenColumns?: QuoteTableColumnsType[];
  columnsToShow?: string[];
  showBillingProfileNumberInBillingProfileColumn?: boolean;
  shouldDisableRequestQuoteCta?: boolean;
}

type RefetchParams =
  | Partial<{
      searchOptions: ConnectionSearchOptions;
      filterOptions: FilterOption;
    }>
  | undefined;

export const QuoteTableContainer = ({
  transactionAccountId,
  label,
  filterOptions = {},
  hiddenColumns,
  columnsToShow,
  showBillingProfileNumberInBillingProfileColumn,
  shouldDisableRequestQuoteCta,
}: QuoteTableContainerProps) => {
  const defaultFilterOptions = getDefaultFilterOptions({ transactionAccountId, filterOptions });
  const [isEnabled] = useFeatureToggle();

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const currentFilterOptions = useRef<FilterOption>();

  const { data, loading, error, refetch } = useFetchQuotesForTransactionAccount({
    transactionAccountId,
    filterOptions,
    shouldFetchLineItems: true,
  });

  const combinedRefetch = (refetchParams: RefetchParams) => {
    // Remove the old status filters from the default options
    /** why this needed ?
     * The quote table will by default opens with the 'OPEN' tab, which shows the quotes that are in the 'OPEN' or 'STALE' state.
     * when user switched to the new tab , for eg - accepted , the new status will be pushed to the query as {and: [{staus: accepted}]}
     * now on the query we will have 3 status , {open or stale} and {accepted} which would cause the error. Hence its imp to remove the default
     * status filters.
     */
    const defaultAndFilters = defaultFilterOptions.and.filter((filter: FilterOption | null) => {
      if (filter?.or) {
        return !filter?.or.some((orFilter: FilterOption | null) => orFilter?.key === 'status');
      }
      return filter?.key !== 'status';
    });

    const filterOptions = {
      //@ts-ignore
      and: [...defaultAndFilters, ...(refetchParams?.filterOptions?.and || [])],
      or: [...defaultFilterOptions.or, ...(refetchParams?.filterOptions?.or || [])],
      not: [...defaultFilterOptions.not, ...(refetchParams?.filterOptions?.not || [])],
    };

    currentFilterOptions.current = filterOptions;

    refetch({
      searchOptions: {
        first: DEFAULT_PAGE_SIZE,
        offset: 0,
        sort: QUOTE_TABLE_DEFAULT_SORT_OPTIONS,
        ...refetchParams?.searchOptions,
      },
      filterOptions,
    });
  };

  const dataState = {
    loading: loading,
    error: error?.toString(),
    data: data?.quotes?.nodes,
    refetch: combinedRefetch,
    totalCount: data?.quotes?.totalCount ?? 0,
  };

  return (
    <div
      className={classNames({
        'tab-container-quote': !isDesignIteration1Enabled,
        'tab-container-quote-with-label': label,
      })}
    >
      <QuoteTable
        dataState={dataState}
        transactionAccountId={transactionAccountId}
        label={label}
        hiddenColumns={hiddenColumns}
        csvFilterOptions={currentFilterOptions.current || defaultFilterOptions}
        {...(isDesignIteration1Enabled && {
          shouldHideCustomiseView: true,
          columns: columnsToShow,
          shouldHideTabs: true,
          showBillingProfileNumberInBillingProfileColumn,
        })}
        shouldDisableRequestQuoteCta={shouldDisableRequestQuoteCta}
      />
    </div>
  );
};
